import React, { useState, useEffect } from 'react';
import { MessageSquare, Users, Clock, Send, Filter, Search, Eye, Archive } from 'lucide-react';
import conversasService from '../services/conversasService';
import { getCurrentUserProfile } from '../services/usuariosService';

const Conversas = () => {
  const [conversas, setConversas] = useState([]);
  const [conversasOriginais, setConversasOriginais] = useState([]);
  const [estatisticas, setEstatisticas] = useState({
    totalConversas: 0,
    totalMensagens: 0,
    totalParticipantes: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtros, setFiltros] = useState({
    materia: '',
    status: '',
    busca: ''
  });
  const [conversaSelecionada, setConversaSelecionada] = useState(null);
  const [mensagensModal, setMensagensModal] = useState([]);
  const [showMensagensModal, setShowMensagensModal] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);

  // Carregar dados iniciais
  useEffect(() => {
    carregarDados();
  }, []);

  // Recarregar quando filtros mudarem ou perfil for carregado
  useEffect(() => {
    if (!loading && conversasOriginais.length > 0) {
      aplicarFiltros();
    }
  }, [filtros, currentUserProfile, conversasOriginais]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError(null);

      // Carregar perfil do usuário logado
      const profileResult = await getCurrentUserProfile();
      if (profileResult.success) {
        setCurrentUserProfile(profileResult.data);
      }

      // Carregar conversas e estatísticas em paralelo
      const [conversasData, estatisticasData] = await Promise.all([
        conversasService.getAllConversas(),
        conversasService.getEstatisticasConversas()
      ]);

      setConversasOriginais(conversasData);
      setConversas(conversasData);
      setEstatisticas(estatisticasData);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar conversas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = () => {
    let conversasFiltradas = [...conversasOriginais];

    // Filtro por escola (se for school admin)
    if (currentUserProfile?.school_adm && currentUserProfile?.school_id) {
      conversasFiltradas = conversasFiltradas.filter(conversa => 
        conversa.aluno?.escola_id === currentUserProfile.school_id
      );
    }

    // Filtro por matéria
    if (filtros.materia) {
      conversasFiltradas = conversasFiltradas.filter(conversa => 
        conversa.materia === filtros.materia
      );
    }

    // Filtro por status
    if (filtros.status) {
      conversasFiltradas = conversasFiltradas.filter(conversa => 
        conversa.status === filtros.status
      );
    }

    // Filtro por busca (nome do aluno)
    if (filtros.busca) {
      const termoBusca = filtros.busca.toLowerCase();
      conversasFiltradas = conversasFiltradas.filter(conversa => 
        conversa.aluno?.nome?.toLowerCase().includes(termoBusca) ||
        conversa.titulo?.toLowerCase().includes(termoBusca)
      );
    }

    setConversas(conversasFiltradas);
  };

  const visualizarMensagens = async (conversa) => {
    try {
      setConversaSelecionada(conversa);
      const mensagens = await conversasService.getMensagensConversa(conversa.id);
      
      console.log('🔍 Debug - Mensagens recebidas:', mensagens);
      console.log('📊 Total de mensagens:', mensagens?.length || 0);
      
      // Verificar se há mensagens de IA
      const mensagensIA = mensagens?.filter(m => m.tipo_mensagem === 'assistant') || [];
      const mensagensUser = mensagens?.filter(m => m.tipo_mensagem === 'user') || [];
      
      console.log('🤖 Mensagens da IA:', mensagensIA.length);
      console.log('👤 Mensagens do usuário:', mensagensUser.length);
      
      setMensagensModal(mensagens);
      setShowMensagensModal(true);
      
      // Scroll automático para o final das mensagens após um pequeno delay
      setTimeout(() => {
        const messagesContainer = document.querySelector('.chat-conversation');
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }, 100);
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
      setError('Erro ao carregar mensagens da conversa.');
    }
  };

  const alterarStatusConversa = async (chatId, novoStatus) => {
    try {
      await conversasService.atualizarStatusConversa(chatId, novoStatus);
      // Recarregar dados
      carregarDados();
    } catch (err) {
      console.error('Erro ao alterar status:', err);
      setError('Erro ao alterar status da conversa.');
    }
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  if (loading && conversas.length === 0) {
    return (
      <div className="conversas">
        <div className="page-header">
          <h1 className="page-title">Conversas</h1>
          <p className="page-subtitle">Carregando conversas...</p>
        </div>
        <div className="loading-spinner">Carregando dados...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="conversas">
        <div className="page-header">
          <h1 className="page-title">Conversas</h1>
          <p className="page-subtitle">Erro ao carregar dados</p>
        </div>
        <div className="error-message">
          {error}
          <button onClick={carregarDados} className="retry-button">Tentar Novamente</button>
        </div>
      </div>
    );
  }

  return (
    <div className="conversas">
      <div className="page-header">
        <h1 className="page-title">Conversas</h1>
        <p className="page-subtitle">Comunicação e discussões acadêmicas</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <MessageSquare size={24} />
            </div>
          </div>
          <div className="stat-value">
            {currentUserProfile?.school_adm && currentUserProfile?.school_id 
              ? conversasOriginais.filter(c => c.aluno?.escola_id === currentUserProfile.school_id).length
              : estatisticas.totalConversas
            }
          </div>
          <div className="stat-label">Total de Conversas</div>
        </div>
        <div className="stat-card success">
          <div className="stat-header">
            <div className="stat-icon success">
              <Users size={24} />
            </div>
          </div>
          <div className="stat-value">
            {currentUserProfile?.school_adm && currentUserProfile?.school_id 
              ? (() => {
                  const conversasEscola = conversasOriginais.filter(c => c.aluno?.escola_id === currentUserProfile.school_id);
                  const participantesUnicos = new Set(conversasEscola.map(c => c.aluno?.id)).size;
                  return participantesUnicos;
                })()
              : estatisticas.totalParticipantes
            }
          </div>
          <div className="stat-label">Participantes</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-header">
            <div className="stat-icon warning">
              <Send size={24} />
            </div>
          </div>
          <div className="stat-value">
            {currentUserProfile?.school_adm && currentUserProfile?.school_id 
              ? conversasOriginais
                  .filter(c => c.aluno?.escola_id === currentUserProfile.school_id)
                  .reduce((total, c) => total + (c.mensagens || 0), 0)
              : estatisticas.totalMensagens
            }
          </div>
          <div className="stat-label">Mensagens Enviadas</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <Search size={16} />
            <input
              type="text"
              placeholder="Buscar por nome do aluno..."
              value={filtros.busca}
              onChange={(e) => handleFiltroChange('busca', e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <Filter size={16} />
            <select
              value={filtros.materia}
              onChange={(e) => handleFiltroChange('materia', e.target.value)}
              className="filter-select"
            >
              <option value="">Todas as Matérias</option>
              <option value="Matemática">Matemática</option>
              <option value="Português">Português</option>
              <option value="Ciências">Ciências</option>
              <option value="História">História</option>
              <option value="Geografia">Geografia</option>
              <option value="Inglês">Inglês</option>
              <option value="Tecnologia">Tecnologia</option>
            </select>
          </div>
          <div className="filter-group">
            <select
              value={filtros.status}
              onChange={(e) => handleFiltroChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="">Todos os Status</option>
              <option value="ativo">Ativo</option>
              <option value="fechado">Fechado</option>
              <option value="arquivado">Arquivado</option>
            </select>
          </div>
        </div>
      </div>

      <div className="content-card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Conversa</th>
              <th>Aluno</th>
              <th>Matéria</th>
              <th>Mensagens</th>
              <th>Última Atividade</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {conversas.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {loading ? 'Carregando conversas...' : 'Nenhuma conversa encontrada'}
                </td>
              </tr>
            ) : (
              conversas.map(conversa => (
                <tr key={conversa.id}>
                  <td>
                    <div className="conversation-title">
                      <MessageSquare size={16} className="conversation-icon" />
                      <div>
                        <div className="title">{conversa.titulo}</div>
                        <div className="subtitle">{conversa.escola} - {conversa.anoEscolar}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="student-info">
                      <div className="name">{conversa.aluno.nome}</div>
                      <div className="email">{conversa.aluno.email}</div>
                    </div>
                  </td>
                  <td>
                    <span className="materia-badge">{conversa.materia}</span>
                  </td>
                  <td>
                    <span className="count-badge">{conversa.mensagens}</span>
                  </td>
                  <td>
                    <div className="last-message">
                      <Clock size={14} />
                      <div>
                        <div>{conversasService.formatarData(conversa.ultimaMensagem)}</div>
                        <div className="time-ago">{conversasService.calcularTempoUltimaMensagem(conversa.ultimaMensagem)}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${conversa.status}`}>
                      {conversa.status === 'ativo' ? 'Ativo' : 
                       conversa.status === 'fechado' ? 'Fechado' : 'Arquivado'}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      <button
                        onClick={() => visualizarMensagens(conversa)}
                        className="action-btn view"
                        title="Visualizar mensagens"
                      >
                        <Eye size={14} />
                      </button>
                      {conversa.status === 'ativo' && (
                        <button
                          onClick={() => alterarStatusConversa(conversa.id, 'arquivado')}
                          className="action-btn archive"
                          title="Arquivar conversa"
                        >
                          <Archive size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de Mensagens */}
      {showMensagensModal && conversaSelecionada && (
        <div className="modal-overlay" onClick={() => setShowMensagensModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="chat-header-info">
                <h3>💬 {conversaSelecionada.titulo}</h3>
                <div className="chat-subtitle">
                  <span className="student-name">👤 {conversaSelecionada.aluno.nome}</span>
                  <span className="subject-tag">📚 {conversaSelecionada.materia}</span>
                  <span className="message-count">💬 {mensagensModal.length} mensagens</span>
                </div>
              </div>
              <button 
                onClick={() => setShowMensagensModal(false)}
                className="close-button"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="chat-conversation">
                {mensagensModal.length === 0 ? (
                  <div className="no-messages">
                    <div className="empty-state">
                      <MessageSquare size={48} className="empty-icon" />
                      <h4>Nenhuma mensagem encontrada</h4>
                      <p>Esta conversa ainda não possui mensagens.</p>
                    </div>
                  </div>
                ) : (
                  <div className="messages-timeline">
                    {mensagensModal.map((mensagem, index) => {
                      const isUser = mensagem.tipo_mensagem === 'user';
                      const isFirstOfType = index === 0 || mensagensModal[index - 1].tipo_mensagem !== mensagem.tipo_mensagem;
                      
                      return (
                        <div key={mensagem.id} className={`message-bubble ${isUser ? 'user-message' : 'ai-message'}`}>
                          {isFirstOfType && (
                            <div className="message-sender">
                              <div className="sender-avatar">
                                {isUser ? '👤' : '🤖'}
                              </div>
                              <span className="sender-name">
                                {isUser ? conversaSelecionada.aluno.nome : 'IA Tutor'}
                              </span>
                              <span className="message-timestamp">
                                {new Date(mensagem.data_envio).toLocaleString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          )}
                          <div className="message-bubble-content">
                            <div className="message-text">
                              {mensagem.conteudo.split('\n').map((linha, i) => (
                                <p key={i} className={linha.trim() === '' ? 'empty-line' : ''}>
                                  {linha.trim() === '' ? '\u00A0' : linha}
                                </p>
                              ))}
                            </div>
                            {mensagem.tokens_usados > 0 && (
                              <div className="message-metadata">
                                <span className="token-info">🔢 {mensagem.tokens_usados} tokens</span>
                                <span className="model-info">⚙️ {mensagem.modelo_ia}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .filters-section {
          background: white;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .filters-row {
          display: flex;
          gap: 16px;
          align-items: center;
          flex-wrap: wrap;
        }
        .filter-group {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 200px;
        }
        .search-input, .filter-select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          flex: 1;
        }
        .search-input:focus, .filter-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .conversation-title {
          display: flex;
          align-items: center;
          gap: 12px;
          font-weight: 500;
        }
        .conversation-title .title {
          font-weight: 600;
          color: #1f2937;
        }
        .conversation-title .subtitle {
          font-size: 12px;
          color: #6b7280;
          margin-top: 2px;
        }
        .conversation-icon {
          color: #3b82f6;
          flex-shrink: 0;
        }
        .student-info .name {
          font-weight: 500;
          color: #1f2937;
        }
        .student-info .email {
          font-size: 12px;
          color: #6b7280;
          margin-top: 2px;
        }
        .materia-badge {
          background: #f0f9ff;
          color: #0369a1;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 600;
        }
        .count-badge {
          background: #eff6ff;
          color: #3b82f6;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }
        .last-message {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #64748b;
          font-size: 12px;
        }
        .last-message .time-ago {
          color: #9ca3af;
          font-size: 11px;
        }
        .status-badge {
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 600;
        }
        .status-badge.ativo {
          background: #dcfce7;
          color: #166534;
        }
        .status-badge.fechado {
          background: #fef3c7;
          color: #92400e;
        }
        .status-badge.arquivado {
          background: #f3f4f6;
          color: #6b7280;
        }
        .actions {
          display: flex;
          gap: 8px;
        }
        .action-btn {
          padding: 6px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        .action-btn.view {
          background: #eff6ff;
          color: #3b82f6;
        }
        .action-btn.view:hover {
          background: #dbeafe;
        }
        .action-btn.archive {
          background: #fef3c7;
          color: #92400e;
        }
        .action-btn.archive:hover {
          background: #fde68a;
        }
        .no-data {
          text-align: center;
          color: #6b7280;
          font-style: italic;
          padding: 40px;
        }
        .loading-spinner {
          text-align: center;
          padding: 40px;
          color: #6b7280;
        }
        .error-message {
          background: #fef2f2;
          color: #dc2626;
          padding: 20px;
          border-radius: 8px;
          text-align: center;
        }
        .retry-button {
          background: #dc2626;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          margin-top: 10px;
          cursor: pointer;
        }
        .retry-button:hover {
          background: #b91c1c;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal-content {
          background: white;
          border-radius: 16px;
          width: 95%;
          max-width: 900px;
          max-height: 85vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        .modal-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 24px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        .chat-header-info h3 {
          margin: 0 0 8px 0;
          font-size: 20px;
          font-weight: 700;
        }
        .chat-subtitle {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          font-size: 14px;
          opacity: 0.9;
        }
        .student-name, .subject-tag, .message-count {
          background: rgba(255, 255, 255, 0.2);
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }
        .close-button {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: white;
          padding: 8px;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s;
        }
        .close-button:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }
        .modal-body {
          padding: 0;
          overflow: hidden;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .chat-conversation {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          background: #f8fafc;
        }
        .messages-timeline {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .message-bubble {
          display: flex;
          flex-direction: column;
          max-width: 80%;
        }
        .message-bubble.user-message {
          align-self: flex-end;
          align-items: flex-end;
        }
        .message-bubble.ai-message {
          align-self: flex-start;
          align-items: flex-start;
        }
        .message-sender {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
          font-size: 13px;
          color: #64748b;
        }
        .sender-avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
        }
        .user-message .sender-avatar {
          background: #dbeafe;
        }
        .ai-message .sender-avatar {
          background: #dcfce7;
        }
        .sender-name {
          font-weight: 600;
          color: #374151;
        }
        .message-timestamp {
          font-size: 11px;
          color: #9ca3af;
        }
        .message-bubble-content {
          position: relative;
        }
        .message-text {
          padding: 12px 16px;
          border-radius: 18px;
          line-height: 1.5;
          font-size: 14px;
          position: relative;
        }
        .user-message .message-text {
          background: #3b82f6;
          color: white;
          border-bottom-right-radius: 4px;
        }
        .ai-message .message-text {
          background: white;
          color: #374151;
          border: 1px solid #e5e7eb;
          border-bottom-left-radius: 4px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .message-text p {
          margin: 0;
          margin-bottom: 8px;
        }
        .message-text p:last-child {
          margin-bottom: 0;
        }
        .message-text p.empty-line {
          margin-bottom: 8px;
          height: 8px;
        }
        .message-metadata {
          display: flex;
          gap: 12px;
          margin-top: 4px;
          font-size: 11px;
          color: #9ca3af;
        }
        .user-message .message-metadata {
          justify-content: flex-end;
        }
        .ai-message .message-metadata {
          justify-content: flex-start;
        }
        .token-info, .model-info {
          background: #f1f5f9;
          padding: 2px 8px;
          border-radius: 8px;
          font-weight: 500;
        }
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #6b7280;
        }
        .empty-icon {
          color: #d1d5db;
          margin-bottom: 16px;
        }
        .empty-state h4 {
          margin: 0 0 8px 0;
          color: #374151;
          font-size: 18px;
        }
        .empty-state p {
          margin: 0;
          font-size: 14px;
        }
        .no-messages {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-height: 300px;
        }
        
        /* Scrollbar personalizada */
        .chat-conversation::-webkit-scrollbar {
          width: 6px;
        }
        .chat-conversation::-webkit-scrollbar-track {
          background: #f1f5f9;
        }
        .chat-conversation::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .chat-conversation::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        
        /* Animações */
        .message-bubble {
          animation: slideIn 0.3s ease-out;
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Responsividade */
        @media (max-width: 768px) {
          .modal-content {
            width: 100%;
            height: 100%;
            max-height: 100vh;
            border-radius: 0;
          }
          .message-bubble {
            max-width: 90%;
          }
          .chat-subtitle {
            flex-direction: column;
            gap: 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default Conversas;
